'use client';
import React, { useEffect } from 'react';
import { X } from 'lucide-react';

interface LRAModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  dark?: boolean;
}

const sizeMap = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
};

export default function LRAModal({ open, onClose, title, children, size = 'md', dark = false }: LRAModalProps) {
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (open) document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 transition-all duration-200"
      style={{
        backgroundColor: open ? 'rgba(0,0,0,0.6)' : 'rgba(0,0,0,0)',
        pointerEvents: open ? 'auto' : 'none',
        opacity: open ? 1 : 0,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div
        className={`${sizeMap[size]} w-full rounded-xl shadow-2xl transition-all duration-200 ${open ? 'scale-100 opacity-100' : 'scale-95 opacity-0'}`}
        style={{
          backgroundColor: dark ? 'var(--dark-card)' : 'var(--card)',
          border: `1px solid ${dark ? 'var(--dark-border)' : 'var(--border)'}`,
        }}
      >
        <div
          className="flex items-center justify-between px-6 py-4 border-b"
          style={{ borderColor: dark ? 'var(--dark-border)' : 'var(--border)' }}
        >
          <h2
            className="text-base font-semibold"
            style={{ color: dark ? 'var(--dark-text)' : 'var(--foreground)' }}
          >
            {title}
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded-md transition-colors duration-150 hover:bg-white/10"
            style={{ color: dark ? 'var(--dark-muted)' : 'var(--muted-foreground)' }}
            aria-label="Close modal"
          >
            <X size={18} />
          </button>
        </div>
        <div className="px-6 py-4">{children}</div>
      </div>
    </div>
  );
}